import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { PaginatedResults, toCamelCase } from '../lib/typeHelper'
import type { CallMediaStatus } from './salesLead'

export type TelephonyCallVendor = 'aircall' | 'dialpad'

export interface TelephonyCallAttachedLead {
  id: string
  name: string
}

export interface TelephonyCallListItem {
  id: string
  vendor: TelephonyCallVendor
  direction: 'inbound' | 'outbound'
  fromNumber: string
  toNumber: string
  startedAt: string
  answeredAt: string | null
  endedAt: string | null
  durationSeconds: number | null
  recordingStatus: CallMediaStatus
  transcriptStatus: CallMediaStatus
  attachedLeads: TelephonyCallAttachedLead[]
}

export type TelephonyCallAttached = 'true' | 'false' | undefined
export type TelephonyCallDirection = 'inbound' | 'outbound' | undefined

export interface TelephonyCallsFilters {
  page?: number
  direction?: TelephonyCallDirection
  attached?: TelephonyCallAttached
  missedOnly?: boolean
  leadId?: string
  /** Matches the text, either number, or the attached lead or its customer (name, DOT, ID). */
  q?: string
  /** A rep's InsureUser ID: what they handled, plus what reached their line unanswered. */
  userId?: string
  /** Inclusive `yyyy-MM-dd` bounds. */
  dateFrom?: string
  dateTo?: string
}

function getTelephonyCalls(params: TelephonyCallsFilters): Promise<PaginatedResults<TelephonyCallListItem>> {
  const queryParams: Record<string, string> = {}
  if (params.page && params.page > 1) queryParams.page = String(params.page)
  if (params.direction) queryParams.direction = params.direction
  if (params.attached) queryParams.attached = params.attached
  if (params.missedOnly) queryParams.missed_only = 'true'
  if (params.leadId) queryParams.lead_id = params.leadId
  if (params.q) queryParams.q = params.q
  if (params.userId) queryParams.user_id = params.userId
  if (params.dateFrom) queryParams.date_from = params.dateFrom
  if (params.dateTo) queryParams.date_to = params.dateTo
  return axios
    .get('/api/v1/insure/telephony/calls/', { params: queryParams })
    .then((r) => toCamelCase(r.data) as PaginatedResults<TelephonyCallListItem>)
}

export function useTelephonyCalls(params: TelephonyCallsFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [CacheKeys.TELEPHONY_CALLS, params],
    queryFn: () => getTelephonyCalls(params),
    enabled: options?.enabled ?? true,
  })
}

export interface AttachTelephonyCallPayload {
  vendor: TelephonyCallVendor
  callId: string
  leadId: string
}

function attachTelephonyCall(payload: AttachTelephonyCallPayload): Promise<{ activityId: string }> {
  return axios
    .post(`/api/v1/insure/telephony/calls/${payload.vendor}/${payload.callId}/attach/`, {
      lead_id: payload.leadId,
    })
    .then((r) => toCamelCase(r.data) as { activityId: string })
}

export function useAttachTelephonyCall() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: attachTelephonyCall,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.TELEPHONY_CALLS] })
      queryClient.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    },
  })
}
